import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
  FileText, Send, Clock, CheckCircle, XCircle, RefreshCw, Loader2, 
  Plus, Eye, ArrowDownCircle, ArrowUpCircle, Inbox, User,
  Calendar, MessageSquare, Download, Upload, X, File, Paperclip,
  ArrowUpDown, ArrowUp, ArrowDown, Archive, RotateCcw, Search
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { InfoBanner } from '../../components/ui/info-banner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth header helper
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const WorkReports = () => {
  const { user } = useAuth();
  const [mainTab, setMainTab] = useState('incoming');
  
  // Reports state
  const [incomingReports, setIncomingReports] = useState([]);
  const [outgoingReports, setOutgoingReports] = useState([]);
  const [archivedReports, setArchivedReports] = useState([]);
  const [reportRequests, setReportRequests] = useState({ sent: [], received: [] });
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [archiveSearchQuery, setArchiveSearchQuery] = useState(''); // Arşiv arama
  
  // Okunmamış rapor sayısı
  const unreadReportsCount = incomingReports.filter(r => !r.read_by?.includes(user?.id)).length;
  
  // Okunmamış rapor isteği sayısı (bana gelen istekler - sadece pending olanlar)
  const unreadRequestsCount = (reportRequests.received || [])
    .filter(r => r.status === 'pending' && !r.read_by?.includes(user?.id)).length;
  
  // Modal states
  const [createReportModalOpen, setCreateReportModalOpen] = useState(false);
  const [requestReportModalOpen, setRequestReportModalOpen] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedType, setSelectedType] = useState('');  // 'report' veya 'request'
  
  // Form states
  const [newReport, setNewReport] = useState({
    title: '',
    content: '',
    to_user_id: '',
    documents: []
  });
  
  const [newReportRequest, setNewReportRequest] = useState({
    target_user_id: '',
    title: '',
    description: '',
    due_date: ''
  });

  // Sorting state
  const [reportSortConfig, setReportSortConfig] = useState({ key: null, direction: 'asc' });
  const [requestSortConfig, setRequestSortConfig] = useState({ key: null, direction: 'asc' });

  // Arşiv açıklaması modal state'leri
  const [archiveReasonModalOpen, setArchiveReasonModalOpen] = useState(false);
  const [archiveReason, setArchiveReason] = useState('');
  const [archiveTargetId, setArchiveTargetId] = useState(null);
  const [archivingItem, setArchivingItem] = useState(false);

  // Rapor cevaplama için state
  const [replyModalOpen, setReplyModalOpen] = useState(false);
  const [replyingToRequest, setReplyingToRequest] = useState(null);
  const [replyReport, setReplyReport] = useState({
    title: '',
    content: '',
    documents: []
  });

  useEffect(() => {
    loadAll();
    loadUsers();
    loadArchivedReports();
  }, []);

  // Rapor isteği detayı açıldığında okundu olarak işaretle
  useEffect(() => {
    const markRequestAsRead = async () => {
      if (detailModalOpen && selectedType === 'request' && selectedItem?.id && selectedItem?.direction === 'received') {
        try {
          await axios.post(`${API}/work/report-requests/${selectedItem.id}/mark-read`, {}, { headers: getAuthHeaders() });
          // Badge sayacını güncelle
          window.dispatchEvent(new Event('unread-counts-updated'));
          // Listeyi yenile - YENİ badge'i kalksın
          loadAll();
        } catch (error) {
          console.error('Okundu işaretleme hatası:', error);
        }
      }
    };
    markRequestAsRead();
  }, [detailModalOpen, selectedType, selectedItem]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const [inRes, outRes, reqRes] = await Promise.all([
        axios.get(`${API}/work/reports/incoming`, { headers }),
        axios.get(`${API}/work/reports/outgoing`, { headers }),
        axios.get(`${API}/work/report-requests/my`, { headers })
      ]);
      setIncomingReports(inRes.data || []);
      setOutgoingReports(outRes.data || []);
      setReportRequests(reqRes.data || { sent: [], received: [] });
    } catch (error) {
      console.error('Raporlar yüklenirken hata:', error);
      toast.error('Raporlar yüklenemedi');
    }
    setLoading(false);
  };

  const loadUsers = async () => {
    try {
      const res = await axios.get(`${API}/work/users`, { headers: getAuthHeaders() });
      setUsers(res.data || []);
    } catch (error) {
      console.error('Kullanıcılar yüklenirken hata:', error);
    }
  };

  // Arşivlenmiş raporları yükle
  const loadArchivedReports = async () => {
    try {
      const res = await axios.get(`${API}/work/reports/archived`, { headers: getAuthHeaders() });
      setArchivedReports(res.data || []);
    } catch (error) {
      console.error('Arşivlenmiş raporlar yüklenirken hata:', error);
    }
  };

  // Raporu arşivle
  const openArchiveReasonModal = (reportId) => {
    setArchiveTargetId(reportId);
    setArchiveReason('');
    setArchiveReasonModalOpen(true);
  };

  const handleConfirmArchiveReport = async () => {
    if (!archiveReason.trim()) {
      toast.error('Arşiv açıklaması zorunludur');
      return;
    }
    
    setArchivingItem(true);
    try {
      await axios.delete(`${API}/work/reports/${archiveTargetId}`, { 
        headers: getAuthHeaders(),
        data: { archive_reason: archiveReason }
      });
      toast.success('Rapor arşivlendi');
      await loadAll();
      await loadArchivedReports();
      setArchiveReasonModalOpen(false);
      setArchiveReason('');
      setArchiveTargetId(null);
    } catch (error) {
      console.error('Arşivleme hatası:', error);
      toast.error('Rapor arşivlenemedi');
    } finally {
      setArchivingItem(false);
    }
  };

  const handleArchiveReport = async (reportId) => {
    openArchiveReasonModal(reportId);
  };

  // Raporu arşivden geri yükle
  const handleRestoreReport = async (reportId) => {
    try {
      const response = await axios.post(`${API}/work/reports/${reportId}/restore`, {}, { headers: getAuthHeaders() });
      if (response.status === 200) {
        toast.success('Rapor arşivden geri yüklendi');
        await loadArchivedReports();
        await loadAll();
      }
    } catch (error) {
      console.error('Geri yükleme hatası:', error);
      toast.error('Rapor geri yüklenemedi');
    }
  };

  // Raporu görüntüle ve okundu olarak işaretle
  const handleViewReport = async (report) => {
    setSelectedItem(report);
    setSelectedType('report');
    setDetailModalOpen(true);
    
    // Eğer rapor kullanıcıya gelmiş ve henüz okunmamışsa, okundu olarak işaretle
    if (report.to_user_id === user?.id && !report.read_by?.includes(user?.id)) {
      try {
        await axios.post(`${API}/work/reports/${report.id}/mark-read`, {}, { headers: getAuthHeaders() });
        // State'i güncelle - raporu okundu olarak işaretle
        setIncomingReports(prev => prev.map(r => 
          r.id === report.id 
            ? { ...r, read_by: [...(r.read_by || []), user.id] }
            : r
        ));
      } catch (error) {
        console.error('Rapor okundu işaretlenemedi:', error);
      }
    }
  };

  const handleCreateReport = async () => {
    if (!newReport.title) {
      toast.error('Rapor başlığı gerekli');
      return;
    }
    
    try {
      // "none" değerini boş stringe çevir
      const reportData = {
        ...newReport,
        to_user_id: newReport.to_user_id === 'none' ? '' : newReport.to_user_id
      };
      await axios.post(`${API}/work/reports`, reportData, { headers: getAuthHeaders() });
      toast.success('Rapor oluşturuldu');
      setCreateReportModalOpen(false);
      setNewReport({ title: '', content: '', to_user_id: '', documents: [] });
      loadAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Rapor oluşturulamadı');
    }
  };

  // Belge yükleme handler
  const handleReportDocUpload = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = () => {
        setNewReport(prev => ({
          ...prev,
          documents: [...prev.documents, {
            name: file.name,
            type: file.type,
            size: file.size,
            data: reader.result
          }]
        }));
      };
      reader.readAsDataURL(file);
    });
  };

  const removeReportDocument = (index) => {
    setNewReport(prev => ({
      ...prev,
      documents: prev.documents.filter((_, i) => i !== index)
    }));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleRequestReport = async () => {
    if (!newReportRequest.title || !newReportRequest.target_user_id) {
      toast.error('Rapor başlığı ve hedef kullanıcı gerekli');
      return;
    }
    
    try {
      await axios.post(`${API}/work/report-requests`, newReportRequest, { headers: getAuthHeaders() });
      toast.success('Rapor isteği gönderildi');
      setRequestReportModalOpen(false);
      setNewReportRequest({ target_user_id: '', title: '', description: '', due_date: '' });
      loadAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Rapor isteği gönderilemedi');
    }
  };

  const handleUpdateReportRequest = async (requestId, status) => {
    try {
      await axios.put(`${API}/work/report-requests/${requestId}`, null, {
        params: { status },
        headers: getAuthHeaders()
      });
      toast.success('Rapor isteği güncellendi');
      loadAll();
      setDetailModalOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Rapor isteği güncellenemedi');
    }
  };

  // Rapor isteğine cevap olarak rapor gönderme
  const handleOpenReplyModal = (request) => {
    setReplyingToRequest(request);
    setReplyReport({
      title: `Cevap: ${request.title}`,
      content: '',
      documents: []
    });
    setReplyModalOpen(true);
    setDetailModalOpen(false);
  };

  const handleReplyDocUpload = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = () => {
        setReplyReport(prev => ({
          ...prev,
          documents: [...prev.documents, {
            name: file.name,
            type: file.type,
            size: file.size,
            data: reader.result
          }]
        }));
      };
      reader.readAsDataURL(file);
    });
  };

  const removeReplyDocument = (index) => {
    setReplyReport(prev => ({
      ...prev,
      documents: prev.documents.filter((_, i) => i !== index)
    }));
  };

  const handleSendReply = async () => {
    if (!replyReport.title) {
      toast.error('Rapor başlığı gerekli');
      return;
    }
    
    try {
      // Rapor gönder
      const reportData = {
        title: replyReport.title,
        content: replyReport.content,
        to_user_id: replyingToRequest.requester_id,
        documents: replyReport.documents,
        reply_to_request_id: replyingToRequest.id
      };
      const reportResponse = await axios.post(`${API}/work/reports`, reportData, { headers: getAuthHeaders() });
      const createdReportId = reportResponse.data.report?.id;
      
      // İsteği accepted olarak güncelle ve cevap raporu ID'sini ekle
      await axios.put(`${API}/work/report-requests/${replyingToRequest.id}`, null, {
        params: { 
          status: 'accepted',
          response_report_id: createdReportId
        },
        headers: getAuthHeaders()
      });
      
      toast.success('Rapor cevabı gönderildi');
      setReplyModalOpen(false);
      setReplyingToRequest(null);
      setReplyReport({ title: '', content: '', documents: [] });
      loadAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Rapor gönderilemedi');
    }
  };

  // Sıralama fonksiyonları
  const handleReportSort = (key) => {
    setReportSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleRequestSort = (key) => {
    setRequestSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Sıralama ikonu
  const SortIcon = ({ columnKey, sortConfig }) => {
    if (sortConfig.key !== columnKey) {
      return <ArrowUpDown className="h-4 w-4 text-gray-400" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="h-4 w-4 text-amber-600" />
      : <ArrowDown className="h-4 w-4 text-amber-600" />;
  };

  // Raporlar için sıralama
  const sortReports = (reports, type) => {
    if (!reportSortConfig.key) return reports;
    
    return [...reports].sort((a, b) => {
      let aValue, bValue;
      
      switch (reportSortConfig.key) {
        case 'title':
          aValue = a.title?.toLowerCase() || '';
          bValue = b.title?.toLowerCase() || '';
          break;
        case 'sender':
          if (type === 'incoming') {
            aValue = (a.from_user?.name || '').toLowerCase();
            bValue = (b.from_user?.name || '').toLowerCase();
          } else {
            aValue = (a.to_user?.name || '').toLowerCase();
            bValue = (b.to_user?.name || '').toLowerCase();
          }
          break;
        case 'status':
          const statusOrder = { approved: 5, accepted: 4, sent: 2, pending: 1, draft: 0, rejected: -1 };
          aValue = statusOrder[a.status] || 0;
          bValue = statusOrder[b.status] || 0;
          break;
        case 'created_at':
          aValue = new Date(a.created_at || 0).getTime();
          bValue = new Date(b.created_at || 0).getTime();
          break;
        default:
          return 0;
      }
      
      if (aValue < bValue) return reportSortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return reportSortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // Rapor istekleri için sıralama
  const sortRequests = (requests) => {
    if (!requestSortConfig.key) return requests;
    
    return [...requests].sort((a, b) => {
      let aValue, bValue;
      
      switch (requestSortConfig.key) {
        case 'title':
          aValue = a.title?.toLowerCase() || '';
          bValue = b.title?.toLowerCase() || '';
          break;
        case 'user':
          aValue = (a.target_user?.name || a.requester?.name || '').toLowerCase();
          bValue = (b.target_user?.name || b.requester?.name || '').toLowerCase();
          break;
        case 'created_at':
          aValue = new Date(a.created_at || 0).getTime();
          bValue = new Date(b.created_at || 0).getTime();
          break;
        case 'due_date':
          aValue = a.due_date ? new Date(a.due_date).getTime() : 0;
          bValue = b.due_date ? new Date(b.due_date).getTime() : 0;
          break;
        case 'status':
          const statusOrder = { approved: 4, accepted: 3, sent: 2, pending: 1, rejected: 0 };
          aValue = statusOrder[a.status] || 0;
          bValue = statusOrder[b.status] || 0;
          break;
        default:
          return 0;
      }
      
      if (aValue < bValue) return requestSortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return requestSortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const getStatusBadge = (status, isReply = false) => {
    const config = {
      draft: { label: 'Taslak', color: 'bg-gray-100 text-gray-700', icon: FileText },
      sent: { label: 'Gönderildi', color: 'bg-blue-100 text-blue-700', icon: Send },
      pending: { label: 'Bekliyor', color: 'bg-amber-100 text-amber-700', icon: Clock },
      accepted: { label: 'Cevaplandı', color: 'bg-green-100 text-green-700', icon: CheckCircle },
      rejected: { label: 'Reddedildi', color: 'bg-red-100 text-red-700', icon: XCircle }
    };
    const c = config[status] || config.pending;
    const Icon = c.icon;
    return (
      <Badge className={`${c.color} gap-1`}>
        <Icon className="h-3 w-3" />
        {c.label}
      </Badge>
    );
  };

  const renderReportTable = (reports, type) => {
    if (reports.length === 0) {
      return (
        <Card>
          <CardContent className="p-8 text-center">
            <FileText className="h-16 w-16 mx-auto mb-4 text-gray-200" />
            <h3 className="text-xl font-bold mb-2">Rapor Bulunmuyor</h3>
            <p className="text-gray-500">Bu kategoride rapor yok</p>
          </CardContent>
        </Card>
      );
    }

    const sortedReports = sortReports(reports, type);

    return (
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-50 select-none"
                  onClick={() => handleReportSort('title')}
                >
                  <div className="flex items-center gap-1">
                    Rapor Başlığı
                    <SortIcon columnKey="title" sortConfig={reportSortConfig} />
                  </div>
                </TableHead>
                <TableHead>Rapor İçeriği</TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-50 select-none"
                  onClick={() => handleReportSort('sender')}
                >
                  <div className="flex items-center gap-1">
                    {type === 'incoming' ? 'Gönderen' : 'Alıcı'}
                    <SortIcon columnKey="sender" sortConfig={reportSortConfig} />
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-50 select-none"
                  onClick={() => handleReportSort('status')}
                >
                  <div className="flex items-center gap-1">
                    Durum
                    <SortIcon columnKey="status" sortConfig={reportSortConfig} />
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-50 select-none"
                  onClick={() => handleReportSort('created_at')}
                >
                  <div className="flex items-center gap-1">
                    Tarih
                    <SortIcon columnKey="created_at" sortConfig={reportSortConfig} />
                  </div>
                </TableHead>
                <TableHead className="text-right">İşlemler</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedReports.map(report => {
                const isUnread = type === 'incoming' && !report.read_by?.includes(user?.id);
                return (
                <TableRow 
                  key={report.id} 
                  data-testid={`report-row-${report.id}`}
                  className={isUnread ? 'bg-blue-50 hover:bg-blue-100' : ''}
                >
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {isUnread && (
                        <Badge className="bg-red-500 text-white text-xs px-1.5 py-0.5 animate-pulse">YENİ</Badge>
                      )}
                      <div>
                        <p className={`font-medium ${isUnread ? 'text-blue-900' : ''}`}>{report.title}</p>
                        {report.reply_to_request_id && (
                          <Badge className="mt-1 bg-purple-100 text-purple-700 gap-1">
                            <MessageSquare className="h-3 w-3" />
                            İstek Cevabı
                          </Badge>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    {report.content ? (
                      <p className="text-sm text-gray-500 truncate max-w-[200px]">{report.content}</p>
                    ) : (
                      <span className="text-gray-400 text-sm">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-gray-400" />
                      {type === 'incoming' ? report.from_user?.name : report.to_user?.name || 'Belirtilmemiş'}
                    </div>
                  </TableCell>
                  <TableCell>{getStatusBadge(report.status)}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 text-sm text-gray-500">
                      <Calendar className="h-4 w-4" />
                      <span>{new Date(report.created_at).toLocaleDateString('tr-TR')}</span>
                      <span className="text-gray-400">-</span>
                      <Clock className="h-3 w-3" />
                      <span>{new Date(report.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex gap-1 justify-end">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleViewReport(report)}
                        data-testid={`view-report-${report.id}`}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        Detay
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleArchiveReport(report.id)}
                        className="text-gray-600 hover:text-gray-800 hover:bg-gray-100"
                        data-testid={`archive-report-${report.id}`}
                      >
                        <Archive className="h-4 w-4 mr-1" />
                        Arşivle
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    );
  };

  const renderRequestTable = (requests, direction) => {
    // Cevaplanan istekleri filtrele - sadece bekleyenleri göster
    // Cevaplanan istekler zaten gelen/giden raporlarda görünüyor
    const pendingRequests = requests.filter(req => req.status === 'pending');
    
    if (pendingRequests.length === 0) {
      return (
        <Card>
          <CardContent className="p-8 text-center">
            <MessageSquare className="h-16 w-16 mx-auto mb-4 text-gray-200" />
            <h3 className="text-xl font-bold mb-2">Bekleyen İstek Bulunmuyor</h3>
            <p className="text-gray-500">{direction === 'sent' ? 'Bekleyen gönderilmiş isteğiniz yok' : 'Bekleyen gelen isteğiniz yok'}</p>
          </CardContent>
        </Card>
      );
    }

    const sortedRequests = sortRequests(pendingRequests);

    return (
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-50 select-none"
                  onClick={() => handleRequestSort('title')}
                >
                  <div className="flex items-center gap-1">
                    İstek Konusu
                    <SortIcon columnKey="title" sortConfig={requestSortConfig} />
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-50 select-none"
                  onClick={() => handleRequestSort('user')}
                >
                  <div className="flex items-center gap-1">
                    {direction === 'sent' ? 'Kime' : 'Kimden'}
                    <SortIcon columnKey="user" sortConfig={requestSortConfig} />
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-50 select-none"
                  onClick={() => handleRequestSort('created_at')}
                >
                  <div className="flex items-center gap-1">
                    Oluşturulma Tarihi
                    <SortIcon columnKey="created_at" sortConfig={requestSortConfig} />
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-50 select-none"
                  onClick={() => handleRequestSort('due_date')}
                >
                  <div className="flex items-center gap-1">
                    Son Tarih
                    <SortIcon columnKey="due_date" sortConfig={requestSortConfig} />
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-50 select-none"
                  onClick={() => handleRequestSort('status')}
                >
                  <div className="flex items-center gap-1">
                    Durum
                    <SortIcon columnKey="status" sortConfig={requestSortConfig} />
                  </div>
                </TableHead>
                <TableHead className="text-right">İşlemler</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedRequests.map(req => {
                const isUnread = direction === 'received' && !req.read_by?.includes(user?.id);
                return (
                <TableRow key={req.id} data-testid={`request-row-${req.id}`} className={isUnread ? 'bg-purple-50' : ''}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div>
                        <p className="font-medium">{req.title}</p>
                        {req.description && (
                          <p className="text-sm text-gray-500 truncate max-w-[200px]">{req.description}</p>
                        )}
                      </div>
                      {isUnread && (
                        <Badge className="bg-purple-500 text-white text-xs animate-pulse">YENİ</Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-gray-400" />
                      {direction === 'sent' ? req.target_user?.name : req.requester?.name}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 text-sm text-gray-500">
                      <Calendar className="h-4 w-4" />
                      <span>{new Date(req.created_at).toLocaleDateString('tr-TR')}</span>
                      <span className="text-gray-400">-</span>
                      <Clock className="h-3 w-3" />
                      <span>{new Date(req.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    {req.due_date ? (
                      <div className="flex items-center gap-1 text-sm">
                        <Calendar className="h-4 w-4 text-gray-400" />
                        {new Date(req.due_date).toLocaleDateString('tr-TR')}
                      </div>
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    {getStatusBadge(req.status)}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => { setSelectedItem({...req, direction}); setSelectedType('request'); setDetailModalOpen(true); }}
                      data-testid={`view-request-${req.id}`}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      Detay
                    </Button>
                  </TableCell>
                </TableRow>
              )})}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6" data-testid="work-reports">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">RAPORLAR</h1>
          <p className="text-gray-500">Gelen ve giden raporlarınızı yönetin</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={loadAll} variant="outline" size="sm" className="gap-2">
            <RefreshCw className="h-4 w-4" />
            Yenile
          </Button>
          <Button 
            onClick={() => setRequestReportModalOpen(true)}
            variant="outline"
            className="gap-2"
            data-testid="request-report-btn"
          >
            <MessageSquare className="h-4 w-4" />
            RAPOR İSTE
          </Button>
          <Button 
            onClick={() => setCreateReportModalOpen(true)}
            className="gap-2 bg-[hsl(var(--brand-700))] hover:bg-[hsl(var(--brand-800))]"
            data-testid="send-report-btn"
          >
            <Send className="h-4 w-4" />
            RAPOR GÖNDER
          </Button>
        </div>
      </div>

      {/* InfoBanner */}
      <InfoBanner storageKey="work-reports-info">
        Raporlar sayfasından ihtiyacınız olan raporları talep edebilir, iletmeniz gereken raporları iletebilir veya gelen raporlarınızı buradan görüntüleyebilirsiniz.
      </InfoBanner>

      {/* İstatistik Kartları */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4 flex items-center gap-3">
            <ArrowDownCircle className="h-8 w-8 text-blue-600" />
            <div>
              <p className="text-2xl font-bold text-blue-700">{incomingReports.length}</p>
              <p className="text-sm text-blue-600">Gelen Rapor</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-orange-50 border-orange-200">
          <CardContent className="p-4 flex items-center gap-3">
            <ArrowUpCircle className="h-8 w-8 text-orange-600" />
            <div>
              <p className="text-2xl font-bold text-orange-700">{outgoingReports.length}</p>
              <p className="text-sm text-orange-600">Giden Rapor</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-purple-50 border-purple-200">
          <CardContent className="p-4 flex items-center gap-3">
            <MessageSquare className="h-8 w-8 text-purple-600" />
            <div>
              <p className="text-2xl font-bold text-purple-700">
                {reportRequests.sent?.length + reportRequests.received?.length || 0}
              </p>
              <p className="text-sm text-purple-600">Rapor İsteği</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40">
          <Loader2 className="h-8 w-8 animate-spin text-[hsl(var(--brand-600))]" />
        </div>
      ) : (
        <Tabs value={mainTab} onValueChange={setMainTab} className="w-full">
          <TabsList className="grid w-full grid-cols-4 mb-4">
            <TabsTrigger value="incoming" className="gap-2" data-testid="incoming-reports-tab">
              <ArrowDownCircle className="h-4 w-4" />
              GELEN RAPORLAR
              {unreadReportsCount > 0 && (
                <Badge className="ml-1 bg-red-500 text-white animate-pulse">{unreadReportsCount} yeni</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="outgoing" className="gap-2" data-testid="outgoing-reports-tab">
              <ArrowUpCircle className="h-4 w-4" />
              GİDEN RAPORLAR
              {outgoingReports.length > 0 && (
                <Badge className="ml-1 bg-orange-500 text-white">{outgoingReports.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="requests" className="gap-2" data-testid="report-requests-tab">
              <MessageSquare className="h-4 w-4" />
              RAPOR İSTEKLERİ
              {unreadRequestsCount > 0 && (
                <Badge className="ml-1 bg-purple-500 text-white animate-pulse">{unreadRequestsCount} yeni</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="archive" className="gap-2" data-testid="archive-reports-tab">
              <Archive className="h-4 w-4" />
              ARŞİV
              {archivedReports.length > 0 && (
                <Badge className="ml-1 bg-gray-500 text-white">{archivedReports.length}</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* GELEN RAPORLAR */}
          <TabsContent value="incoming" className="mt-4">
            {renderReportTable(incomingReports, 'incoming')}
          </TabsContent>

          {/* GİDEN RAPORLAR */}
          <TabsContent value="outgoing" className="mt-4">
            {renderReportTable(outgoingReports, 'outgoing')}
          </TabsContent>

          {/* RAPOR İSTEKLERİ */}
          <TabsContent value="requests" className="mt-4">
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Send className="h-5 w-5 text-purple-600" />
                    Gönderdiğim İstekler
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {renderRequestTable(reportRequests.sent || [], 'sent')}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <ArrowDownCircle className="h-5 w-5 text-blue-600" />
                    Bana Gelen İstekler
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {renderRequestTable(reportRequests.received || [], 'received')}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* ARŞİV */}
          <TabsContent value="archive" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-gray-600">
                    <Archive className="h-5 w-5" />
                    Arşivlenmiş Raporlar
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        placeholder="Arşivde ara..."
                        value={archiveSearchQuery}
                        onChange={(e) => setArchiveSearchQuery(e.target.value)}
                        className="pl-10 w-64"
                        data-testid="reports-archive-search"
                      />
                    </div>
                    <Badge variant="outline">
                      {archivedReports.filter(r => 
                        !archiveSearchQuery || 
                        r.title?.toLowerCase().includes(archiveSearchQuery.toLowerCase()) ||
                        r.from_user?.name?.toLowerCase().includes(archiveSearchQuery.toLowerCase()) ||
                        r.from_user_name?.toLowerCase().includes(archiveSearchQuery.toLowerCase()) ||
                        r.to_user?.name?.toLowerCase().includes(archiveSearchQuery.toLowerCase()) ||
                        r.to_user_name?.toLowerCase().includes(archiveSearchQuery.toLowerCase())
                      ).length} rapor
                    </Badge>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {archivedReports.filter(r => 
                  !archiveSearchQuery || 
                  r.title?.toLowerCase().includes(archiveSearchQuery.toLowerCase()) ||
                  r.from_user?.name?.toLowerCase().includes(archiveSearchQuery.toLowerCase()) ||
                  r.from_user_name?.toLowerCase().includes(archiveSearchQuery.toLowerCase()) ||
                  r.to_user?.name?.toLowerCase().includes(archiveSearchQuery.toLowerCase()) ||
                  r.to_user_name?.toLowerCase().includes(archiveSearchQuery.toLowerCase())
                ).length === 0 ? (
                  <div className="text-center py-12">
                    <Archive className="h-16 w-16 mx-auto mb-4 text-gray-200" />
                    <h3 className="text-xl font-bold mb-2 text-gray-600">Arşivde Rapor Yok</h3>
                    <p className="text-gray-500">Arşivlenmiş rapor bulunmuyor</p>
                  </div>
                ) : (
                  <div className="max-h-[500px] overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead className="font-bold">Rapor</TableHead>
                        <TableHead className="font-bold">Gönderen</TableHead>
                        <TableHead className="font-bold">Alan</TableHead>
                        <TableHead className="font-bold">Tarih</TableHead>
                        <TableHead className="font-bold">Arşiv Tarihi</TableHead>
                        <TableHead className="font-bold text-right">İşlemler</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {archivedReports.filter(r => 
                        !archiveSearchQuery || 
                        r.title?.toLowerCase().includes(archiveSearchQuery.toLowerCase()) ||
                        r.from_user?.name?.toLowerCase().includes(archiveSearchQuery.toLowerCase()) ||
                        r.from_user_name?.toLowerCase().includes(archiveSearchQuery.toLowerCase()) ||
                        r.to_user?.name?.toLowerCase().includes(archiveSearchQuery.toLowerCase()) ||
                        r.to_user_name?.toLowerCase().includes(archiveSearchQuery.toLowerCase())
                      ).map(report => (
                        <TableRow key={report.id} className="hover:bg-gray-50" data-testid={`archived-report-row-${report.id}`}>
                          <TableCell>
                            <div className="font-medium">{report.title}</div>
                            {report.content && (
                              <div className="text-sm text-gray-500 truncate max-w-[200px]">{report.content}</div>
                            )}
                          </TableCell>
                          <TableCell>{report.from_user?.name || report.from_user_name || '-'}</TableCell>
                          <TableCell>{report.to_user?.name || report.to_user_name || '-'}</TableCell>
                          <TableCell>
                            {report.created_at ? new Date(report.created_at).toLocaleDateString('tr-TR', {
                              day: '2-digit', month: '2-digit', year: 'numeric'
                            }) : '-'}
                          </TableCell>
                          <TableCell>
                            {report.archived_at ? new Date(report.archived_at).toLocaleDateString('tr-TR', {
                              day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
                            }) : '-'}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex gap-1 justify-end">
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => { setSelectedItem(report); setSelectedType('report'); setDetailModalOpen(true); }}
                                data-testid={`view-archived-report-${report.id}`}
                              >
                                <Eye className="h-4 w-4 mr-1" />
                                Detay
                              </Button>
                              <Button 
                                variant="default"
                                size="sm"
                                onClick={() => handleRestoreReport(report.id)}
                                className="bg-green-600 hover:bg-green-700"
                                data-testid={`restore-report-${report.id}`}
                              >
                                <RotateCcw className="h-4 w-4 mr-1" />
                                Geri Yükle
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}

      {/* Rapor Oluşturma Modalı */}
      <Dialog open={createReportModalOpen} onOpenChange={setCreateReportModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5 text-[hsl(var(--brand-600))]" />
              Yeni Rapor Oluştur
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="report-title">Rapor Başlığı *</Label>
              <Input 
                id="report-title"
                value={newReport.title}
                onChange={(e) => setNewReport({ ...newReport, title: e.target.value })}
                placeholder="Rapor başlığını girin"
                data-testid="report-title-input"
              />
            </div>
            
            <div>
              <Label htmlFor="report-content">Rapor İçeriği</Label>
              <Textarea 
                id="report-content"
                value={newReport.content}
                onChange={(e) => setNewReport({ ...newReport, content: e.target.value })}
                placeholder="Rapor içeriği"
                rows={5}
                data-testid="report-content-input"
              />
            </div>
            
            <div>
              <Label htmlFor="report-user">Gönderilecek Kişi (Opsiyonel)</Label>
              <Select 
                value={newReport.to_user_id} 
                onValueChange={(val) => setNewReport({ ...newReport, to_user_id: val })}
              >
                <SelectTrigger data-testid="report-user-select">
                  <SelectValue placeholder="Kullanıcı seçin (boş bırakılabilir)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Belirtilmemiş</SelectItem>
                  {users.map(u => (
                    <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Belge Yükleme Alanı */}
            <div>
              <Label className="flex items-center gap-2">
                <Paperclip className="h-4 w-4 text-blue-500" />
                Belge Ekle
              </Label>
              <div className="mt-2">
                <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed rounded-lg cursor-pointer hover:bg-gray-50 border-gray-300">
                  <div className="flex flex-col items-center justify-center pt-2 pb-2">
                    <Upload className="w-6 h-6 mb-1 text-gray-400" />
                    <p className="text-sm text-gray-500">Belge yüklemek için tıklayın</p>
                    <p className="text-xs text-gray-400">PDF, JPG, PNG, DOC, XLS</p>
                  </div>
                  <input
                    type="file"
                    className="hidden"
                    multiple
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                    onChange={handleReportDocUpload}
                    data-testid="report-file-input"
                  />
                </label>
              </div>
              {newReport.documents.length > 0 && (
                <div className="mt-2 space-y-2">
                  {newReport.documents.map((doc, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex items-center gap-2">
                        <File className="h-4 w-4 text-blue-500" />
                        <span className="text-sm truncate max-w-[200px]">{doc.name}</span>
                        <span className="text-xs text-gray-400">({formatFileSize(doc.size)})</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeReportDocument(index)}
                        className="text-red-500 hover:text-red-700 p-1"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateReportModalOpen(false)}>
              İptal
            </Button>
            <Button 
              onClick={handleCreateReport}
              className="bg-[hsl(var(--brand-700))] hover:bg-[hsl(var(--brand-800))]"
              data-testid="submit-report-btn"
            >
              <Send className="h-4 w-4 mr-2" />
              Rapor Gönder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rapor İsteği Modalı */}
      <Dialog open={requestReportModalOpen} onOpenChange={setRequestReportModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-purple-600" />
              Rapor İste
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="request-user">Kimden İstiyorsunuz? *</Label>
              <Select 
                value={newReportRequest.target_user_id} 
                onValueChange={(val) => setNewReportRequest({ ...newReportRequest, target_user_id: val })}
              >
                <SelectTrigger data-testid="request-user-select">
                  <SelectValue placeholder="Kullanıcı seçin" />
                </SelectTrigger>
                <SelectContent>
                  {users.map(u => (
                    <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="request-title">Rapor Konusu *</Label>
              <Input 
                id="request-title"
                value={newReportRequest.title}
                onChange={(e) => setNewReportRequest({ ...newReportRequest, title: e.target.value })}
                placeholder="İstenen raporun konusu"
                data-testid="request-title-input"
              />
            </div>
            
            <div>
              <Label htmlFor="request-desc">Açıklama</Label>
              <Textarea 
                id="request-desc"
                value={newReportRequest.description}
                onChange={(e) => setNewReportRequest({ ...newReportRequest, description: e.target.value })}
                placeholder="Rapor hakkında detaylı açıklama"
                rows={3}
                data-testid="request-desc-input"
              />
            </div>
            
            <div>
              <Label htmlFor="request-due">Son Tarih</Label>
              <Input 
                id="request-due"
                type="date"
                value={newReportRequest.due_date}
                onChange={(e) => setNewReportRequest({ ...newReportRequest, due_date: e.target.value })}
                data-testid="request-due-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setRequestReportModalOpen(false)}>
              İptal
            </Button>
            <Button 
              onClick={handleRequestReport}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="submit-request-btn"
            >
              <Send className="h-4 w-4 mr-2" />
              İsteği Gönder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detay Modalı */}
      <Dialog open={detailModalOpen} onOpenChange={setDetailModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-[hsl(var(--brand-600))]" />
              {selectedType === 'report' ? 'Rapor Detayı' : 'Rapor İsteği Detayı'}
            </DialogTitle>
          </DialogHeader>
          
          {selectedItem && selectedType === 'report' && (
            <div className="space-y-4">
              {/* Orijinal İstek Bilgileri - Eğer bu rapor bir isteğe cevap ise */}
              {selectedItem.reply_to_request_id && selectedItem.original_request && (
                <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                  <div className="flex items-center gap-2 mb-3">
                    <MessageSquare className="h-5 w-5 text-purple-600" />
                    <h4 className="font-semibold text-purple-800">Orijinal İstek Bilgileri</h4>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <Label className="text-purple-600 text-sm">İstek Başlığı</Label>
                      <p className="font-medium text-gray-800">{selectedItem.original_request.title || '-'}</p>
                    </div>
                    {selectedItem.original_request.description && (
                      <div>
                        <Label className="text-purple-600 text-sm">İstek Açıklaması</Label>
                        <p className="text-gray-700 whitespace-pre-wrap bg-white p-2 rounded mt-1">{selectedItem.original_request.description}</p>
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-purple-600 text-sm">Talep Eden</Label>
                        <p className="font-medium text-gray-800">{selectedItem.original_request.requester_name || '-'}</p>
                      </div>
                      <div>
                        <Label className="text-purple-600 text-sm">İstek Tarihi</Label>
                        <p className="font-medium text-gray-800">
                          {selectedItem.original_request.created_at 
                            ? new Date(selectedItem.original_request.created_at).toLocaleDateString('tr-TR')
                            : '-'}
                        </p>
                      </div>
                    </div>
                    {selectedItem.original_request.due_date && (
                      <div>
                        <Label className="text-purple-600 text-sm">Talep Edilen Teslim Tarihi</Label>
                        <p className="font-medium text-gray-800">
                          {new Date(selectedItem.original_request.due_date).toLocaleDateString('tr-TR')}
                        </p>
                      </div>
                    )}
                    {/* Orijinal istekteki belgeler */}
                    {selectedItem.original_request.documents && selectedItem.original_request.documents.length > 0 && (
                      <div>
                        <Label className="text-purple-600 text-sm flex items-center gap-1">
                          <Paperclip className="h-3 w-3" />
                          İstekteki Belgeler ({selectedItem.original_request.documents.length})
                        </Label>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {selectedItem.original_request.documents.map((doc, index) => (
                            <a
                              key={index}
                              href={doc.url || doc.data}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 bg-white rounded-lg px-3 py-2 text-sm text-purple-700 hover:bg-purple-100 border border-purple-200"
                            >
                              <File className="h-4 w-4" />
                              {doc.name}
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Cevap/Rapor Bilgileri */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <FileText className="h-5 w-5 text-blue-600" />
                  <h3 className="font-bold text-lg">{selectedItem.reply_to_request_id ? 'İstek Cevabı' : selectedItem.title}</h3>
                </div>
                {selectedItem.reply_to_request_id && !selectedItem.original_request && (
                  <Badge className="mb-2 bg-purple-100 text-purple-700 gap-1">
                    <MessageSquare className="h-3 w-3" />
                    Bu rapor bir isteğe cevap olarak gönderilmiştir
                  </Badge>
                )}
                {selectedItem.content && (
                  <p className="text-gray-600 whitespace-pre-wrap">{selectedItem.content}</p>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">Gönderen</Label>
                  <p className="font-medium">{selectedItem.from_user?.name || selectedItem.from_user_name || '-'}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Alıcı</Label>
                  <p className="font-medium">{selectedItem.to_user?.name || 'Belirtilmemiş'}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Durum</Label>
                  <div className="mt-1">{getStatusBadge(selectedItem.status)}</div>
                </div>
                <div>
                  <Label className="text-gray-500">Tarih ve Saat</Label>
                  <p className="font-medium">
                    {new Date(selectedItem.created_at).toLocaleDateString('tr-TR')} - {new Date(selectedItem.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>

              {/* Ekli Belgeler */}
              {selectedItem.documents && selectedItem.documents.length > 0 && (
                <div className="pt-4 border-t">
                  <Label className="text-gray-500 mb-2 block flex items-center gap-2">
                    <Paperclip className="h-4 w-4" />
                    Ekli Belgeler ({selectedItem.documents.length})
                  </Label>
                  <div className="space-y-2">
                    {selectedItem.documents.map((doc, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-blue-100 rounded-lg">
                            <File className="h-5 w-5 text-blue-600" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-700">{doc.name}</p>
                            <p className="text-xs text-gray-400">{formatFileSize(doc.size)}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {doc.data && (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  const newWindow = window.open();
                                  if (newWindow) {
                                    newWindow.document.write(`
                                      <html>
                                        <head><title>${doc.name}</title></head>
                                        <body style="margin:0;display:flex;justify-content:center;align-items:center;min-height:100vh;background:#f5f5f5;">
                                          ${doc.type?.startsWith('image/') 
                                            ? `<img src="${doc.data}" style="max-width:100%;max-height:100vh;" />`
                                            : doc.type === 'application/pdf'
                                              ? `<iframe src="${doc.data}" style="width:100%;height:100vh;border:none;"></iframe>`
                                              : `<div style="padding:20px;background:white;border-radius:8px;box-shadow:0 2px 10px rgba(0,0,0,0.1);"><p>Bu dosya türü önizlenemez.</p><p>Lütfen indirin.</p></div>`
                                          }
                                        </body>
                                      </html>
                                    `);
                                  }
                                }}
                                className="gap-1"
                                data-testid={`view-report-doc-${index}`}
                              >
                                <Eye className="h-4 w-4" />
                                Görüntüle
                              </Button>
                              <Button
                                variant="default"
                                size="sm"
                                onClick={() => {
                                  const link = document.createElement('a');
                                  link.href = doc.data;
                                  link.download = doc.name;
                                  document.body.appendChild(link);
                                  link.click();
                                  document.body.removeChild(link);
                                }}
                                className="gap-1 bg-blue-600 hover:bg-blue-700"
                                data-testid={`download-report-doc-${index}`}
                              >
                                <Download className="h-4 w-4" />
                                İndir
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {selectedItem && selectedType === 'request' && (
            <div className="space-y-4">
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h3 className="font-bold text-lg mb-2">{selectedItem.title}</h3>
                {selectedItem.description && (
                  <p className="text-gray-600">{selectedItem.description}</p>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">İsteyen</Label>
                  <p className="font-medium">{selectedItem.requester?.name || selectedItem.requester_name || '-'}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Hedef</Label>
                  <p className="font-medium">{selectedItem.target_user?.name || selectedItem.target_user_name || '-'}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Oluşturulma Tarihi</Label>
                  <p className="font-medium">
                    {selectedItem.created_at ? (
                      <>{new Date(selectedItem.created_at).toLocaleDateString('tr-TR')} - {new Date(selectedItem.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}</>
                    ) : '-'}
                  </p>
                </div>
                <div>
                  <Label className="text-gray-500">Durum</Label>
                  <div className="mt-1">{getStatusBadge(selectedItem.status)}</div>
                </div>
                {selectedItem.due_date && (
                  <div>
                    <Label className="text-gray-500">Son Tarih</Label>
                    <p className="font-medium">{new Date(selectedItem.due_date).toLocaleDateString('tr-TR')}</p>
                  </div>
                )}
              </div>

              {/* Cevap Raporu - eğer varsa */}
              {selectedItem.response_report && (
                <div className="pt-4 border-t">
                  <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                    <div className="flex items-center gap-2 mb-3">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                      <h4 className="font-bold text-green-800">Cevap Raporu</h4>
                    </div>
                    
                    <div className="space-y-3">
                      <div>
                        <Label className="text-gray-500 text-sm">Rapor Başlığı</Label>
                        <p className="font-medium">{selectedItem.response_report.title}</p>
                      </div>
                      
                      {selectedItem.response_report.content && (
                        <div>
                          <Label className="text-gray-500 text-sm">Rapor İçeriği</Label>
                          <p className="text-gray-600 whitespace-pre-wrap text-sm bg-white p-3 rounded border">
                            {selectedItem.response_report.content}
                          </p>
                        </div>
                      )}
                      
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-gray-500 text-sm">Gönderen</Label>
                          <p className="font-medium text-sm">{selectedItem.response_report.from_user?.name || '-'}</p>
                        </div>
                        <div>
                          <Label className="text-gray-500 text-sm">Gönderim Tarihi</Label>
                          <p className="font-medium text-sm">
                            {selectedItem.response_report.created_at 
                              ? new Date(selectedItem.response_report.created_at).toLocaleDateString('tr-TR') + ' - ' + 
                                new Date(selectedItem.response_report.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })
                              : '-'}
                          </p>
                        </div>
                      </div>
                      
                      {/* Cevap Raporunun Belgeleri */}
                      {selectedItem.response_report.documents && selectedItem.response_report.documents.length > 0 && (
                        <div className="pt-3 border-t border-green-200">
                          <Label className="text-gray-500 text-sm mb-2 flex items-center gap-2">
                            <Paperclip className="h-4 w-4" />
                            Ekli Belgeler ({selectedItem.response_report.documents.length})
                          </Label>
                          <div className="space-y-2 mt-2">
                            {selectedItem.response_report.documents.map((doc, index) => (
                              <div key={index} className="flex items-center justify-between p-2 bg-white rounded-lg border border-green-200 hover:bg-green-50 transition-colors">
                                <div className="flex items-center gap-2">
                                  <div className="p-1.5 bg-green-100 rounded">
                                    <File className="h-4 w-4 text-green-600" />
                                  </div>
                                  <div>
                                    <p className="text-sm font-medium text-gray-700">{doc.name}</p>
                                    <p className="text-xs text-gray-400">{formatFileSize(doc.size)}</p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-1">
                                  {doc.data && (
                                    <>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => {
                                          const newWindow = window.open();
                                          if (newWindow) {
                                            newWindow.document.write(`
                                              <html>
                                                <head><title>${doc.name}</title></head>
                                                <body style="margin:0;display:flex;justify-content:center;align-items:center;min-height:100vh;background:#f5f5f5;">
                                                  ${doc.type?.startsWith('image/') 
                                                    ? `<img src="${doc.data}" style="max-width:100%;max-height:100vh;" />`
                                                    : doc.type === 'application/pdf'
                                                      ? `<iframe src="${doc.data}" style="width:100%;height:100vh;border:none;"></iframe>`
                                                      : `<div style="padding:20px;background:white;border-radius:8px;box-shadow:0 2px 10px rgba(0,0,0,0.1);"><p>Bu dosya türü önizlenemez.</p><p>Lütfen indirin.</p></div>`
                                                  }
                                                </body>
                                              </html>
                                            `);
                                          }
                                        }}
                                        className="h-7 px-2 gap-1 text-xs"
                                      >
                                        <Eye className="h-3 w-3" />
                                        Görüntüle
                                      </Button>
                                      <Button
                                        variant="default"
                                        size="sm"
                                        onClick={() => {
                                          const link = document.createElement('a');
                                          link.href = doc.data;
                                          link.download = doc.name;
                                          document.body.appendChild(link);
                                          link.click();
                                          document.body.removeChild(link);
                                        }}
                                        className="h-7 px-2 gap-1 text-xs bg-green-600 hover:bg-green-700"
                                      >
                                        <Download className="h-3 w-3" />
                                        İndir
                                      </Button>
                                    </>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Bana gelen istek ve bekliyor durumunda - Cevapla/Reddet butonları */}
              {selectedItem.direction === 'received' && selectedItem.status === 'pending' && (
                <div className="flex gap-2 pt-4 border-t">
                  <Button 
                    onClick={() => handleOpenReplyModal(selectedItem)}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                    data-testid="reply-request-btn"
                  >
                    <Send className="h-4 w-4 mr-2" />
                    Cevapla
                  </Button>
                  <Button 
                    onClick={() => handleUpdateReportRequest(selectedItem.id, 'rejected')}
                    variant="destructive"
                    className="flex-1"
                    data-testid="reject-request-btn"
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    Reddet
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Cevaplama Modalı */}
      <Dialog open={replyModalOpen} onOpenChange={setReplyModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="h-5 w-5 text-green-600" />
              Rapor İsteğine Cevap Ver
            </DialogTitle>
          </DialogHeader>
          
          {replyingToRequest && (
            <div className="space-y-4">
              {/* İstek Bilgisi */}
              <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
                <p className="text-sm text-purple-600 mb-1">Cevap verilen istek:</p>
                <p className="font-medium">{replyingToRequest.title}</p>
                {replyingToRequest.description && (
                  <p className="text-sm text-gray-600 mt-1">{replyingToRequest.description}</p>
                )}
                <p className="text-xs text-gray-500 mt-2">
                  İsteyen: {replyingToRequest.requester?.name || replyingToRequest.requester_name}
                </p>
              </div>
              
              <div>
                <Label htmlFor="reply-title">Rapor Başlığı *</Label>
                <Input 
                  id="reply-title"
                  value={replyReport.title}
                  onChange={(e) => setReplyReport({ ...replyReport, title: e.target.value })}
                  placeholder="Rapor başlığını girin"
                  data-testid="reply-title-input"
                />
              </div>
              
              <div>
                <Label htmlFor="reply-content">Rapor İçeriği</Label>
                <Textarea 
                  id="reply-content"
                  value={replyReport.content}
                  onChange={(e) => setReplyReport({ ...replyReport, content: e.target.value })}
                  placeholder="Rapor içeriği"
                  rows={5}
                  data-testid="reply-content-input"
                />
              </div>

              {/* Belge Yükleme Alanı */}
              <div>
                <Label className="flex items-center gap-2">
                  <Paperclip className="h-4 w-4 text-blue-500" />
                  Belge Ekle
                </Label>
                <div className="mt-2">
                  <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed rounded-lg cursor-pointer hover:bg-gray-50 border-gray-300">
                    <div className="flex flex-col items-center justify-center pt-2 pb-2">
                      <Upload className="w-6 h-6 mb-1 text-gray-400" />
                      <p className="text-sm text-gray-500">Belge yüklemek için tıklayın</p>
                      <p className="text-xs text-gray-400">PDF, JPG, PNG, DOC, XLS</p>
                    </div>
                    <input
                      type="file"
                      className="hidden"
                      multiple
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                      onChange={handleReplyDocUpload}
                      data-testid="reply-file-input"
                    />
                  </label>
                </div>
                {replyReport.documents.length > 0 && (
                  <div className="mt-2 space-y-2">
                    {replyReport.documents.map((doc, index) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="flex items-center gap-2">
                          <File className="h-4 w-4 text-blue-500" />
                          <span className="text-sm truncate max-w-[200px]">{doc.name}</span>
                          <span className="text-xs text-gray-400">({formatFileSize(doc.size)})</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeReplyDocument(index)}
                          className="text-red-500 hover:text-red-700 p-1"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setReplyModalOpen(false);
              setReplyingToRequest(null);
              setReplyReport({ title: '', content: '', documents: [] });
            }}>
              İptal
            </Button>
            <Button 
              onClick={handleSendReply}
              className="bg-green-600 hover:bg-green-700"
              data-testid="submit-reply-btn"
            >
              <Send className="h-4 w-4 mr-2" />
              Cevabı Gönder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Arşiv Açıklaması Modal */}
      <Dialog open={archiveReasonModalOpen} onOpenChange={setArchiveReasonModalOpen}>
        <DialogContent className="max-w-md" data-testid="archive-reason-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-600">
              <Archive className="h-5 w-5" />
              Raporu Arşive Gönder
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-gray-600">
              Bu raporu arşive göndermek için bir açıklama yazmanız gerekmektedir.
            </p>
            <div className="space-y-2">
              <Label htmlFor="archive-reason" className="text-sm font-medium">
                Arşiv Açıklaması <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="archive-reason"
                placeholder="Arşivleme nedenini yazın..."
                value={archiveReason}
                onChange={(e) => setArchiveReason(e.target.value)}
                className="min-h-[100px]"
                data-testid="archive-reason-input"
              />
              <p className="text-xs text-gray-500">
                Bu açıklama arşivlenen raporun detaylarında görünecektir.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setArchiveReasonModalOpen(false);
                setArchiveReason('');
                setArchiveTargetId(null);
              }}
              disabled={archivingItem}
            >
              İptal
            </Button>
            <Button 
              onClick={handleConfirmArchiveReport}
              className="bg-orange-600 hover:bg-orange-700"
              disabled={archivingItem || !archiveReason.trim()}
              data-testid="confirm-archive-btn"
            >
              {archivingItem ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Archive className="h-4 w-4 mr-2" />
              )}
              Arşive Gönder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WorkReports;
